import { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectCurrentCompanyId, selectCurrentCoopData, selectToken, selectUserType } from "../store/auth";
import { useGetStaffInvestorPortalSettingsQuery } from "../store/admin/investorKyc/staffInvestorKycApi";
import { useGetInvestorProfileQuery } from "../store/investor/investorAuthApi";
import { isStaffUserType } from "../utils/investorAccess";
import { extractCapabilitiesFromCoopData, mapApiCapabilitiesToPortal } from "../utils/investorCapabilities";

/**
 * Company capability flags for investor portal features.
 * Resolved from cooperative data, staff settings, and/or investor profile.
 */
export const CAPABILITY_KEYS = {
  INVESTOR_PORTAL: "investorPortal",
  INVESTMENT: "investment",
  MARKETPLACE: "marketplace",
  COOPERATIVE_REQUEST: "cooperativeRequest",
  REAL_ESTATE: "realEstate",
};

const DISABLED_CAPABILITIES = {
  [CAPABILITY_KEYS.INVESTOR_PORTAL]: false,
  [CAPABILITY_KEYS.INVESTMENT]: false,
  [CAPABILITY_KEYS.MARKETPLACE]: false,
  [CAPABILITY_KEYS.COOPERATIVE_REQUEST]: false,
  [CAPABILITY_KEYS.REAL_ESTATE]: false,
};

function resolveFromApiPayload(coopData, staffSettingsPayload, profileCapabilities, isStaff) {
  const fromCoop = extractCapabilitiesFromCoopData(coopData);
  const staffEffective = staffSettingsPayload?.effective_capabilities;
  const fromStaff = staffEffective ? mapApiCapabilitiesToPortal(staffEffective) : null;
  const fromProfile = profileCapabilities ? mapApiCapabilitiesToPortal(profileCapabilities) : null;

  // Staff portal: effective_capabilities from module assignments only — never login coop cache.
  const merged = isStaff
    ? { ...DISABLED_CAPABILITIES, ...(fromStaff || {}) }
    : {
        ...DISABLED_CAPABILITIES,
        ...(fromCoop || {}),
        ...(fromStaff || {}),
        ...(fromProfile || {}),
      };

  return {
    capabilities: merged,
    investorSignupEnabled: Boolean(
      (fromStaff && fromStaff.investorSignupEnabled) ||
        (fromProfile && fromProfile.investorSignupEnabled) ||
        (fromCoop && fromCoop.investorSignupEnabled)
    ),
  };
}

export function useCompanyCapabilities() {
  const companyId = useSelector(selectCurrentCompanyId);
  const coopData = useSelector(selectCurrentCoopData);
  const token = useSelector(selectToken);
  const userType = useSelector(selectUserType);
  const isStaff = isStaffUserType(userType) && Boolean(token);

  const { data: staffSettings } = useGetStaffInvestorPortalSettingsQuery(undefined, {
    skip: !isStaff,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  });

  const { data: investorProfile } = useGetInvestorProfileQuery(undefined, {
    skip: isStaff || !token,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  });

  const resolved = useMemo(
    () => resolveFromApiPayload(coopData, staffSettings, investorProfile?.capabilities, isStaff),
    [coopData, staffSettings, investorProfile, isStaff]
  );

  const { capabilities, investorSignupEnabled } = resolved;

  return {
    companyId,
    capabilities,
    investorSignupEnabled,
    investorPortal: Boolean(capabilities[CAPABILITY_KEYS.INVESTOR_PORTAL]),
    investment: Boolean(capabilities[CAPABILITY_KEYS.INVESTMENT]),
    marketplace: Boolean(capabilities[CAPABILITY_KEYS.MARKETPLACE]),
    cooperativeRequest: Boolean(capabilities[CAPABILITY_KEYS.COOPERATIVE_REQUEST]),
    realEstate: Boolean(capabilities[CAPABILITY_KEYS.REAL_ESTATE]),
    hasAnyInvestorFeature:
      Boolean(capabilities[CAPABILITY_KEYS.INVESTOR_PORTAL]) ||
      Boolean(capabilities[CAPABILITY_KEYS.INVESTMENT]) ||
      Boolean(capabilities[CAPABILITY_KEYS.MARKETPLACE]) ||
      Boolean(capabilities[CAPABILITY_KEYS.COOPERATIVE_REQUEST]) ||
      Boolean(capabilities[CAPABILITY_KEYS.REAL_ESTATE]),
  };
}
